import { useState, type FormEvent, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import Reveal from './Reveal'
import {
  BRIEF_EMAIL,
  EMPTY_BRIEF,
  PROJECT_TYPES,
  STAGES,
  TIMELINES,
  buildBriefMailto,
  sendBrief,
  validateBrief,
  type BriefInput,
} from '../lib/brief'

type Status = 'idle' | 'sending' | 'sent' | 'handoff' | 'error'

const statusText: Record<Status, string> = {
  idle: 'Your details go only to our inbox.',
  sending: 'Sending your brief.',
  sent: 'Thanks. We have your brief and reply within one business day.',
  handoff: `Your email app should open with the brief filled in. If nothing opened, write to ${BRIEF_EMAIL} directly.`,
  error: `We could not send your brief. Please email ${BRIEF_EMAIL} directly.`,
}

type Errors = Partial<Record<keyof BriefInput, string>>

const inputClass =
  'site-focus w-full bg-ledger border border-[#2A3B5E] text-white placeholder:text-mist/60 rounded-sm px-4 py-3 text-base focus:border-vault transition-colors duration-150 ease-out'

function ChoiceGroup({
  legend,
  name,
  options,
  value,
  error,
  onChange,
}: {
  legend: string
  name: keyof BriefInput
  options: readonly { value: string; label: string; hint?: string }[]
  value: string
  error?: string
  onChange: (name: keyof BriefInput, value: string) => void
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="font-brandSans font-semibold text-base text-white mb-4">{legend}</legend>
      <div className="grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
        {options.map((opt) => {
          const checked = value === opt.value
          return (
            <label
              key={opt.value}
              className={`relative flex items-start gap-3 cursor-pointer border px-4 py-3.5 rounded-sm transition-colors duration-150 ease-out focus-within:ring-2 focus-within:ring-vault focus-within:ring-offset-2 focus-within:ring-offset-mariner ${
                checked ? 'border-vault bg-ledger' : 'border-[#2A3B5E] hover:border-mist'
              }`}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={checked}
                onChange={() => onChange(name, opt.value)}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className={`mt-0.5 w-4 h-4 shrink-0 border flex items-center justify-center rounded-full ${
                  checked ? 'border-vault bg-vault text-mariner' : 'border-mist'
                }`}
              >
                {checked && <Check className="w-3 h-3" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-base font-medium text-white">{opt.label}</span>
                {opt.hint && <span className="block text-sm text-mist mt-0.5">{opt.hint}</span>}
              </span>
            </label>
          )
        })}
      </div>
      {error && (
        <p role="alert" className="text-sm text-vault mt-3">
          {error}
        </p>
      )}
    </fieldset>
  )
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white mb-2">
        {label}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-vault mt-2">
          {error}
        </p>
      )}
    </div>
  )
}

export default function Brief() {
  const [form, setForm] = useState<BriefInput>(EMPTY_BRIEF)
  const [errors, setErrors] = useState<Errors>({})
  const [status, setStatus] = useState<Status>('idle')

  const update = (name: keyof BriefInput, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const found = validateBrief(form)
    setErrors(found)
    if (Object.keys(found).length > 0) {
      const first = Object.keys(found)[0]
      document.getElementById(first)?.focus()
      return
    }

    const endpoint = import.meta.env.VITE_BRIEF_ENDPOINT as string | undefined
    if (!endpoint) {
      // No form service configured: hand the brief to the visitor's own email app.
      window.location.href = buildBriefMailto(form)
      setStatus('handoff')
      return
    }

    setStatus('sending')
    try {
      await sendBrief(form, endpoint)
      setStatus('sent')
      setForm(EMPTY_BRIEF)
    } catch {
      setStatus('error')
    }
  }

  return (
    <section
      id="brief"
      aria-labelledby="brief-title"
      className="relative bg-mariner text-white pt-32 md:pt-40 pb-24 md:pb-32 [clip-path:polygon(0_6%,100%_0,100%_100%,0_100%)] -mt-16 md:-mt-24"
    >
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <p className="site-label text-vault">05 · Start a project</p>
            <h2 id="brief-title" className="site-display text-4xl md:text-5xl mt-5">
              Tell us what you are building.
            </h2>
            <p className="text-lg text-mist leading-relaxed mt-6">
              Answer three quick questions and add a few details. We reply within one business day.
            </p>
            <p className="text-base text-mist leading-relaxed mt-6">
              Send your brief from this page. We reply within one business day.
            </p>
            <p className="text-base mt-10">
              Prefer email?{' '}
              <a href={`mailto:${BRIEF_EMAIL}`} className="site-btn-quiet site-focus text-vault hover:text-white">
                {BRIEF_EMAIL}
              </a>
            </p>
          </Reveal>

          <Reveal delay={0.06} className="lg:col-span-6 lg:col-start-7">
            <form noValidate onSubmit={onSubmit} className="space-y-12" aria-describedby="brief-note">
              <div className="space-y-10">
                <p className="font-brandMono text-sm text-mist">Step 1 of 3 · Your project</p>
                <ChoiceGroup
                  legend="What kind of work is it?"
                  name="projectType"
                  options={PROJECT_TYPES}
                  value={form.projectType}
                  error={errors.projectType}
                  onChange={update}
                />
                <ChoiceGroup
                  legend="Where is it today?"
                  name="stage"
                  options={STAGES}
                  value={form.stage}
                  error={errors.stage}
                  onChange={update}
                />
                <ChoiceGroup
                  legend="When do you want to start?"
                  name="timeline"
                  options={TIMELINES}
                  value={form.timeline}
                  error={errors.timeline}
                  onChange={update}
                />
              </div>

              <div className="space-y-6">
                <p className="font-brandMono text-sm text-mist">Step 2 of 3 · Your details</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field id="name" label="Your name" error={errors.name}>
                    <input
                      id="name"
                      type="text"
                      autoComplete="name"
                      className={inputClass}
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                  </Field>
                  <Field id="email" label="Work email" error={errors.email}>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className={inputClass}
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  </Field>
                </div>
                <Field id="company" label="Company (optional)">
                  <input
                    id="company"
                    type="text"
                    autoComplete="organization"
                    className={inputClass}
                    value={form.company}
                    onChange={(e) => update('company', e.target.value)}
                  />
                </Field>
              </div>

              <div className="space-y-6">
                <p className="font-brandMono text-sm text-mist">Step 3 of 3 · The brief</p>
                <Field id="note" label="What are you building, and what is hard about it today?">
                  <textarea
                    id="note"
                    rows={5}
                    className={inputClass}
                    value={form.note}
                    onChange={(e) => update('note', e.target.value)}
                  />
                </Field>
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="site-btn-primary site-focus w-full sm:w-auto disabled:opacity-60"
                >
                  {status === 'sending' ? 'Sending' : 'Send the brief'}
                </button>
                <p id="brief-note" role="status" aria-live="polite" className={`text-sm ${status === 'error' ? 'text-vault' : 'text-mist'}`}>
                  {statusText[status]}
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
